/**
 * useBEStudy Hook
 * Main hook for BE Study Automation System with localStorage persistence
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  MOCK_USERS,
  MOCK_PROMPTS,
  MOCK_SETTINGS,
  MOCK_ADMIN_DASHBOARD,
  MOCK_USER_DASHBOARD,
  MOCK_REVIEWER_DASHBOARD,
  USER_ROLES,
  USER_STATUS,
  AI_MODELS,
  SUPPORTED_FILE_FORMATS,
} from '@/constants/beStudyMockData';

const STORAGE_KEYS = {
  USERS: 'be_study_users',
  PROMPTS: 'be_study_prompts',
  SETTINGS: 'be_study_settings',
};

/**
 * Get initial data from localStorage or use mock data
 */
const getInitialData = (key, mockData) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error(`Error loading ${key} from localStorage:`, error);
  }
  return mockData;
};

/**
 * Save data to localStorage
 */
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

export function useBEStudy() {
  // Users state
  const [users, setUsers] = useState(() => getInitialData(STORAGE_KEYS.USERS, MOCK_USERS));
  
  // Prompts state
  const [prompts, setPrompts] = useState(() => getInitialData(STORAGE_KEYS.PROMPTS, MOCK_PROMPTS));
  
  // Settings state
  const [settings, setSettings] = useState(() => getInitialData(STORAGE_KEYS.SETTINGS, MOCK_SETTINGS));
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist to localStorage whenever data changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.USERS, users);
  }, [users]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.PROMPTS, prompts);
  }, [prompts]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.SETTINGS, settings);
  }, [settings]);

  // ============================================
  // USER MANAGEMENT
  // ============================================

  const generateUserId = useCallback(() => {
    const maxId = users.reduce((max, u) => {
      const num = parseInt(u.id.replace('USR-', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `USR-${String(maxId + 1).padStart(3, '0')}`;
  }, [users]);

  const getAllUsers = useCallback(() => users, [users]);

  const getUser = useCallback((id) => users.find(u => u.id === id), [users]);

  const getUsersByRoleFilter = useCallback((role) => {
    if (!role) return users;
    return users.filter(u => u.role === role);
  }, [users]);

  const createUser = useCallback((userData) => {
    const newUser = {
      id: generateUserId(),
      ...userData,
      status: USER_STATUS.ACTIVE,
      avatar: null,
      createdAt: new Date().toISOString(),
      lastLogin: null,
    };
    
    setUsers(prev => [...prev, newUser]);
    return { success: true, user: newUser };
  }, [generateUserId]);

  const updateUser = useCallback((id, updates) => {
    setUsers(prev =>
      prev.map(u => (u.id === id ? { ...u, ...updates } : u))
    );
    return { success: true };
  }, []);

  const deleteUser = useCallback((id) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    return { success: true };
  }, []);

  const toggleUserStatus = useCallback((id) => {
    setUsers(prev =>
      prev.map(u => {
        if (u.id !== id) return u;
        return {
          ...u,
          status: u.status === USER_STATUS.ACTIVE ? USER_STATUS.INACTIVE : USER_STATUS.ACTIVE,
        };
      })
    );
    return { success: true };
  }, []);

  const getReviewers = useCallback(() => {
    return users.filter(u => u.role === USER_ROLES.REVIEWER && u.status === USER_STATUS.ACTIVE);
  }, [users]);

  // ============================================
  // PROMPT MANAGEMENT
  // ============================================

  const generatePromptId = useCallback(() => {
    const maxId = prompts.reduce((max, p) => {
      const num = parseInt(p.id.replace('PRM-', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `PRM-${String(maxId + 1).padStart(3, '0')}`;
  }, [prompts]);

  const getAllPrompts = useCallback(() => prompts, [prompts]);

  const getPrompt = useCallback((id) => prompts.find(p => p.id === id), [prompts]);

  const getActivePrompts = useCallback(() => {
    return prompts.filter(p => p.isActive);
  }, [prompts]);

  const createPrompt = useCallback((promptData) => {
    const newPrompt = {
      id: generatePromptId(),
      ...promptData,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setPrompts(prev => [...prev, newPrompt]);
    return { success: true, prompt: newPrompt };
  }, [generatePromptId]);

  const updatePrompt = useCallback((id, updates) => {
    setPrompts(prev =>
      prev.map(p =>
        p.id === id
          ? { ...p, ...updates, updatedAt: new Date().toISOString() }
          : p
      )
    );
    return { success: true };
  }, []);

  const deletePrompt = useCallback((id) => {
    setPrompts(prev => prev.filter(p => p.id !== id));
    return { success: true };
  }, []);

  const togglePromptStatus = useCallback((id) => {
    setPrompts(prev =>
      prev.map(p => {
        if (p.id !== id) return p;
        return { ...p, isActive: !p.isActive, updatedAt: new Date().toISOString() };
      })
    );
    return { success: true };
  }, []);

  // ============================================
  // SETTINGS MANAGEMENT
  // ============================================

  const getSettings = useCallback(() => settings, [settings]);

  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
    return { success: true };
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(MOCK_SETTINGS);
    localStorage.removeItem(STORAGE_KEYS.SETTINGS);
    return { success: true };
  }, []);

  // ============================================
  // DASHBOARD DATA
  // ============================================

  const getAdminDashboard = useCallback(() => {
    return {
      ...MOCK_ADMIN_DASHBOARD,
      metrics: {
        ...MOCK_ADMIN_DASHBOARD.metrics,
        totalUsers: users.length,
        activeUsers: users.filter(u => u.status === USER_STATUS.ACTIVE).length,
      },
    };
  }, [users]);

  const getUserDashboard = useCallback((userId) => {
    return MOCK_USER_DASHBOARD;
  }, []);

  const getReviewerDashboard = useCallback((reviewerId) => {
    return MOCK_REVIEWER_DASHBOARD;
  }, []);

  // ============================================
  // STATISTICS
  // ============================================

  const userStatistics = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.status === USER_STATUS.ACTIVE).length,
    inactive: users.filter(u => u.status === USER_STATUS.INACTIVE).length,
    admins: users.filter(u => u.role === USER_ROLES.ADMIN).length,
    regularUsers: users.filter(u => u.role === USER_ROLES.USER).length,
    reviewers: users.filter(u => u.role === USER_ROLES.REVIEWER).length,
  }), [users]);

  const promptStatistics = useMemo(() => ({
    total: prompts.length,
    active: prompts.filter(p => p.isActive).length,
    inactive: prompts.filter(p => !p.isActive).length,
  }), [prompts]);

  // ============================================
  // RESET ALL DATA
  // ============================================

  const resetAllData = useCallback(() => {
    setUsers(MOCK_USERS);
    setPrompts(MOCK_PROMPTS);
    setSettings(MOCK_SETTINGS);
    localStorage.removeItem(STORAGE_KEYS.USERS);
    localStorage.removeItem(STORAGE_KEYS.PROMPTS);
    localStorage.removeItem(STORAGE_KEYS.SETTINGS);
  }, []);

  return {
    // Users
    users,
    getAllUsers,
    getUser,
    getUsersByRole: getUsersByRoleFilter,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    getReviewers,
    userStatistics,
    
    // Prompts
    prompts,
    getAllPrompts,
    getPrompt,
    getActivePrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    togglePromptStatus,
    promptStatistics,
    
    // Settings
    settings,
    getSettings,
    updateSettings,
    resetSettings,
    
    // Dashboard
    getAdminDashboard,
    getUserDashboard,
    getReviewerDashboard,
    
    // Constants
    aiModels: AI_MODELS,
    supportedFormats: SUPPORTED_FILE_FORMATS,
    userRoles: USER_ROLES,
    userStatuses: USER_STATUS,
    
    // Loading
    isLoading,
    error,

    // Reset
    resetAllData,
  };
}

export default useBEStudy;
